import { useAtomValue } from "jotai";
import { type FunctionComponent, type ReactNode, useMemo } from "react";
import { graphql, useFragment } from "react-relay";

import { ClientSideSuspense } from "shared/components/common/client-side-suspense";
import type { section as sectionData } from "shared/data/section";

import { rosettaAtom } from "scmp-app/lib/rosetta";
import type { plusWidgetQuery$key } from "scmp-app/queries/__generated__/plusWidgetQuery.graphql";

import { usePlusWidgetConfig } from "./hooks";
import { PlusWidgetHasAccess } from "./plus-widget-has-access";
import { PlusWidgetWithoutAccess } from "./plus-widget-without-access";
import { Container, ContentContainer, ContentWrapper, Placeholder } from "./styles";

export enum PlusWidgetTheme {
  DARK = "dark",
  LIGHT = "light",
}

export type PlusWidgetsSampleContentType =
  | "homepage"
  | null
  | typeof sectionData.business.aiEngineName
  | typeof sectionData.china.aiEngineName
  | typeof sectionData.chinaFutureTech.aiEngineName
  | typeof sectionData.economy.aiEngineName
  | typeof sectionData.hongKong.aiEngineName;

type Props = {
  className?: string;
  fallbackWidget?: () => ReactNode;
  pageType: "homepage" | "section" | "topic";
  reference: plusWidgetQuery$key;
  sampleContent: PlusWidgetsSampleContentType;
};

export const PlusWidget: FunctionComponent<Props> = ({
  className,
  fallbackWidget,
  pageType,
  reference,
  sampleContent,
}) => {
  const asyncRosettaState = useAtomValue(rosettaAtom);
  const hasScmpPlusAccessRight = asyncRosettaState?.result?.hasScmpPlusAccessRight ?? false;
  const data = useFragment(
    graphql`
      fragment plusWidgetQuery on Query
      @argumentDefinitions(
        applicationId: { type: "String!" }
        dailyPulseTypeUuId: { type: "String!" }
        highlightQueueName: { type: "String!" }
        plusNewsAgendaEndDate: { type: "Timestamp!" }
        plusNewsAgendaStartDate: { type: "Timestamp!" }
        plusNewsAgendaTypes: { type: "[String]!" }
      ) {
        ...plusWidgetWithoutAccess
          @arguments(
            applicationId: $applicationId
            dailyPulseTypeUuId: $dailyPulseTypeUuId
            highlightQueueName: $highlightQueueName
            plusNewsAgendaEndDate: $plusNewsAgendaEndDate
            plusNewsAgendaStartDate: $plusNewsAgendaStartDate
            plusNewsAgendaTypes: $plusNewsAgendaTypes
          )
        ...plusWidgetHasAccessQuery
          @arguments(
            applicationId: $applicationId
            dailyPulseTypeUuId: $dailyPulseTypeUuId
            highlightQueueName: $highlightQueueName
            plusNewsAgendaEndDate: $plusNewsAgendaEndDate
            plusNewsAgendaStartDate: $plusNewsAgendaStartDate
            plusNewsAgendaTypes: $plusNewsAgendaTypes
          )
        ...hooksPlusWidgetConfigQuery
      }
    `,
    reference,
  );

  const hasPianoExperienceExecuted = asyncRosettaState?.result?.hasPianoExperienceExecuted ?? false;
  const plusWidgetTheme = hasScmpPlusAccessRight ? PlusWidgetTheme.LIGHT : PlusWidgetTheme.DARK;

  const { plusWidgetConfig } = usePlusWidgetConfig(data);

  const isWidgetDisabled = useMemo(
    () =>
      plusWidgetConfig?.[
        hasScmpPlusAccessRight ? "withAccess" : "withoutAccess"
      ]?.disabledDateRanges?.some(({ end, start }) => {
        const isAfterStart = new Date() >= new Date(start);
        const isBeforeEnd = end ? new Date() <= new Date(end) : true; // If no end date, assume open-ended
        return isAfterStart && isBeforeEnd;
      }) ?? false,
    [hasScmpPlusAccessRight, plusWidgetConfig],
  );

  if (asyncRosettaState?.status !== "success" || !hasPianoExperienceExecuted) {
    return (
      <Container className={className}>
        <Placeholder />
      </Container>
    );
  }

  if (isWidgetDisabled) {
    return fallbackWidget?.();
  }

  return (
    <Container className={className}>
      <ClientSideSuspense fallback={<Placeholder />}>
        <ContentContainer $plusWidgetTheme={plusWidgetTheme}>
          <ContentWrapper>
            {hasScmpPlusAccessRight ? (
              <PlusWidgetHasAccess
                pageType={pageType}
                plusWidgetTheme={plusWidgetTheme}
                reference={data}
              />
            ) : (
              <PlusWidgetWithoutAccess
                pageType={pageType}
                reference={data}
                sampleContent={sampleContent}
              />
            )}
          </ContentWrapper>
        </ContentContainer>
      </ClientSideSuspense>
    </Container>
  );
};

PlusWidget.displayName = "PlusWidget";
